import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Grid,
    Divider,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import Layout from "../Util/Layout";
import {
    DroneIcon,
    CalculatorIcon,
    ToolkitIcon,
    BoatSensorIcon,
    NetworkIcon,
    StonksIcon,
    ArcAgiIcon,
    MemoryLookupIcon,
} from "../../Assets/SVGs";

// Mirrors the Articles index: category sections, card per item, hand-written summary.
// Each project links to its existing detail page.
const PROJECT_SECTIONS = [
    {
        category: "AI & Machine Learning",
        projects: [
            {
                title: "ALAN",
                link: "/ala-navigator",
                icon: NetworkIcon,
                summary:
                    "ALAN, the ALA Navigator, leverages perception data from a complex sensor suite, an advanced software stack, and high performance computing to deliver an autonomous cattle feeding solution.",
            },
            {
                title: "ARC-AGI Solver",
                link: "/arc-agi-project",
                icon: ArcAgiIcon,
                summary:
                    "An AI agent that solves ARC-AGI problems — both seen and unseen during training — at acceptable accuracy. NumPy is used heavily for grid manipulation and the search over candidate transformation programs.",
            },
            {
                title: "ML Stock Trading",
                link: "/ml-trading-project",
                icon: StonksIcon,
                summary:
                    "A fully functioning market simulation with both a manual trading strategy and an agentic Q-Learning trading strategy. Built for my Georgia Tech Master of Science in Computer Science coursework.",
            },
            {
                title: "KVMN Question Answering Agent",
                link: "/kvmn-question-answering",
                icon: MemoryLookupIcon,
                summary:
                    "A Key-Value Memory Network built from scratch in PyTorch that answers natural language questions by attending over an external fact dictionary instead of memorizing anything in its weights.",
            },
            {
                title: "AI Premium Calculation",
                link: "/ai-premium-calculation",
                icon: CalculatorIcon,
                summary:
                    "An automated data ingestion system that taps into telematics data via APIs and uses a risk-analysis model to recommend commercial insurance premiums nationwide.",
            },
        ],
    },
    {
        category: "Drones & Field Systems",
        projects: [
            {
                title: "Mission Review",
                link: "/mission-review",
                icon: DroneIcon,
                summary:
                    "An AWS serverless application that makes drone flight data easy to share and troubleshoot, leveraging the full AWS tool suite. Built around data ingestion and manipulation at scale.",
            },
            {
                title: "A.W.C.S.D.R",
                link: "/water-collaborative",
                icon: BoatSensorIcon,
                summary:
                    "Short for Aerial Water Collaborative Sensor Deployment and Recovery — an unmanned vessel deployed by drones in the arctic to monitor beaver populations.",
            },
        ],
    },
    {
        category: "Tooling & Automation",
        projects: [
            {
                title: "InvenTree Toolkit",
                link: "/inventree-toolkit",
                icon: ToolkitIcon,
                summary:
                    "An inventory management tool suite that automates everyday tasks for administrative workers. Taps into the open-source InvenTree APIs and saves the company over $1k per week.",
            },
        ],
    },
];

const ProjectsPage = () => {
    return (
        <Layout>
            <Box p={3}>
                <Box mb={4}>
                    <Typography variant="h3" component="h1" gutterBottom>
                        Projects
                    </Typography>
                    <Typography
                        color="text.secondary"
                        sx={{ fontSize: "1.05rem", lineHeight: 1.6 }}
                    >
                        A selection of personal and professional projects I've built over the
                        years. Click any card to read the full write-up.
                    </Typography>
                </Box>

                {PROJECT_SECTIONS.map(({ category, projects }) => (
                    <Box key={category} mb={5}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Typography
                                variant="overline"
                                sx={{
                                    letterSpacing: 1.2,
                                    color: "primary.main",
                                    fontWeight: 600,
                                }}
                            >
                                {category}
                            </Typography>
                            <Divider sx={{ flex: 1, ml: 2 }} />
                        </Box>
                        <Grid container spacing={3}>
                            {projects.map((project) => {
                                const Icon = project.icon;
                                return (
                                    <Grid item xs={12} sm={6} md={6} key={project.title}>
                                        <Card
                                            elevation={0}
                                            sx={{
                                                height: "100%",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                transition:
                                                    "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                                                "&:hover": {
                                                    transform: "translateY(-2px)",
                                                    boxShadow: 3,
                                                    borderColor: "primary.main",
                                                },
                                            }}
                                        >
                                            <CardActionArea
                                                component={RouterLink}
                                                to={project.link}
                                                sx={{ height: "100%", alignItems: "stretch" }}
                                            >
                                                <CardContent
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        height: "100%",
                                                    }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        mb={1.5}
                                                        sx={{ gap: 1.5 }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 44,
                                                                height: 44,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                borderRadius: 1.5,
                                                                bgcolor: "primary.main",
                                                                color: "primary.contrastText",
                                                                flexShrink: 0,
                                                                "& svg": {
                                                                    width: 26,
                                                                    height: 26,
                                                                },
                                                            }}
                                                        >
                                                            <Icon />
                                                        </Box>
                                                        <Chip label={category} size="small" />
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        component="h2"
                                                        sx={{ fontWeight: 600, mb: 1 }}
                                                    >
                                                        {project.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mb: 2 }}
                                                    >
                                                        {project.summary}
                                                    </Typography>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        sx={{
                                                            mt: "auto",
                                                            color: "primary.main",
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        <Typography variant="button">
                                                            View project
                                                        </Typography>
                                                        <ArrowForwardIcon
                                                            fontSize="small"
                                                            sx={{ ml: 0.5 }}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                ))}
            </Box>
        </Layout>
    );
};

export default ProjectsPage;
